'use client'

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  IconArrowRight,
  IconBook2,
  IconCards,
  IconFileText,
  IconFlag,
  IconLanguage,
  IconMusic,
  IconPlus,
  IconRoute,
  IconTarget
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { LoginDialog } from '@/components/auth/login-dialog'
import { PageLayout } from '@/components/layout/page-layout'
import { StoryCard } from '@/components/story-card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent
} from '@/components/ui/accordion'
import { useAuth } from '@/hooks/useAuth'
import { useDashboardStats } from '@/hooks/useDashboardStats'
import {
  useDocumentSummaries,
  type UserDocumentSummary
} from '@/hooks/useDocuments'
import {
  useLearningPreferences,
  useUpsertLearningPreferences
} from '@/hooks/useLearningPreferences'
import { useLearningStates } from '@/hooks/useLearningState'
import { useMediaHistory } from '@/hooks/useMediaHistory'
import {
  useReaderContentSummaries,
  type StoredReaderContentSummary
} from '@/hooks/useReaderContents'
import type { StoryLearningIndexItem } from '@/content/stories'
import type { GrammarDifficulty } from '@/content/grammar/schema'
import { Link, locales } from '@/i18n/routing'
import { analytics } from '@/lib/analytics'
import {
  buildContentFamiliaritySummary,
  type ContentFamiliaritySummary
} from '@/lib/familiarity-core'
import {
  getGrammarFamiliarityStatuses,
  getWordFamiliarityStatuses
} from '@/lib/familiarity'
import {
  buildPathSections,
  resolvePathSections,
  type PathGrammarItem,
  type PathStoryItem,
  type ResolvedPathNode,
  type ResolvedPathSection
} from '@/lib/path-core'
import { canonicalRoutes } from '@/lib/product-routes'
import {
  readOnboardingState,
  shouldShowExistingUserOnboardingPrompt
} from '@/lib/onboarding'
import {
  learningContentKey,
  grammarLearningLocator,
  storyLearningLocator
} from '@/lib/learning-state-core'
import {
  progressPercent,
  getStoryProgressKind
} from '@/lib/content-progress-core'
import {
  readRecentLocalReaderProgress,
  READER_PROGRESS_CHANGED_EVENT,
  type LocalReaderProgressRecord
} from '@/lib/reader/progress'
import { getRecentDefaultStudyItems } from '@/lib/study'
import { cn } from '@/lib/utils'
import type { StudyDeckItem } from '@/types/study'
import type { UserMediaHistoryItem } from '@/lib/media-history'

export type GrammarPathIndexItem = {
  conceptId: string
  order: number
  title: string
  shortTitle?: string
  summary?: string
  href: string
  form?: string
  difficulty?: GrammarDifficulty
  storyCount: number
  firstStoryHref: string | null
  firstStoryTitle: string | null
}

type ReaderResume = {
  title: string
  subtitle: string
  href: string
  sourceType: string
}

type ReaderResumeCandidate = ReaderResume & {
  timestamp: number
}

type PathVolumeSummary = {
  volume: number
  stepCount: number
}

function formatCount(value: number) {
  return value.toLocaleString()
}

function percent(value: number | null | undefined) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0
  return Math.round(Math.max(0, Math.min(1, value)) * 100)
}

function stableIdsKey(values: Array<number | string>) {
  return values.join(',')
}

function getReaderTimestamp(item: StoredReaderContentSummary) {
  return new Date(
    item.last_opened_at || item.updated_at || item.created_at
  ).getTime()
}

function normalizeLocalRouteHref(href: string) {
  let localHref = href

  try {
    if (href.startsWith('http://') || href.startsWith('https://')) {
      const url = new URL(href)
      localHref = `${url.pathname}${url.search}${url.hash}`
    }
  } catch {
    localHref = href
  }

  const firstSegment = localHref.match(/^\/([^/?#]+)(.*)$/)
  if (!firstSegment) return localHref

  const [, maybeLocale, rest = ''] = firstSegment
  if (!locales.includes(maybeLocale as (typeof locales)[number])) {
    return localHref
  }

  return rest.startsWith('/') ? rest : `/${rest}`
}

function getReaderResume(
  readerItems: StoredReaderContentSummary[] | undefined,
  documents: UserDocumentSummary[] | undefined,
  localProgress: LocalReaderProgressRecord | null
): ReaderResume | null {
  const stored = [...(readerItems ?? [])].sort(
    (a, b) => getReaderTimestamp(b) - getReaderTimestamp(a)
  )[0]

  const storedResume: ReaderResumeCandidate | null = stored
    ? {
        title: stored.title,
        subtitle: stored.subtitle || stored.source_type,
        href:
          stored.source_type === 'lyrics'
            ? `/read/lyrics/${stored.id}`
            : `/read/document/${stored.id}`,
        sourceType: stored.source_type,
        timestamp: getReaderTimestamp(stored)
      }
    : null

  const document = [...(documents ?? [])].sort(
    (a, b) =>
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  )[0]

  const documentResume: ReaderResumeCandidate | null = document
    ? {
        title: document.title,
        subtitle: document.file_name || document.file_type,
        href: `/read/document/${document.id}`,
        sourceType: 'document',
        timestamp: new Date(document.updated_at).getTime()
      }
    : null

  const localResume: ReaderResumeCandidate | null = localProgress
    ? {
        title: localProgress.title,
        subtitle: `${localProgress.sourceType} ${localProgress.percent}%`,
        href: normalizeLocalRouteHref(localProgress.href),
        sourceType: localProgress.sourceType,
        timestamp: new Date(localProgress.updatedAt).getTime()
      }
    : null

  const best = [storedResume, documentResume, localResume]
    .filter((item): item is ReaderResumeCandidate => Boolean(item))
    .sort((a, b) => b.timestamp - a.timestamp)[0]

  return best
    ? {
        title: best.title,
        subtitle: best.subtitle,
        href: best.href,
        sourceType: best.sourceType
      }
    : null
}

function getStudyItemTitle(item: StudyDeckItem) {
  const fields = item.fields ?? {}
  if (typeof fields.text === 'string' && fields.text) return fields.text
  if (typeof fields.simplified === 'string' && fields.simplified) {
    return fields.simplified
  }
  return item.front
}

function usePathStoryReadiness(stories: StoryLearningIndexItem[]) {
  const { user } = useAuth()
  const storyKey = useMemo(
    () => stableIdsKey(stories.map((story) => story.slug)),
    [stories]
  )
  const wordIds = useMemo(
    () =>
      Array.from(new Set(stories.flatMap((story) => story.wordIds))).sort(
        (a, b) => a - b
      ),
    [stories]
  )
  const grammarIds = useMemo(
    () =>
      Array.from(
        new Set(stories.flatMap((story) => story.grammarConceptIds))
      ).sort((a, b) => a.localeCompare(b)),
    [stories]
  )

  return useQuery({
    queryKey: [
      'path-story-readiness',
      user?.id,
      storyKey,
      stableIdsKey(wordIds),
      stableIdsKey(grammarIds)
    ],
    queryFn: async () => {
      const [wordStatuses, grammarStatuses] = await Promise.all([
        getWordFamiliarityStatuses(wordIds),
        getGrammarFamiliarityStatuses(grammarIds)
      ])

      return new Map(
        stories.map((story) => [
          story.slug,
          buildContentFamiliaritySummary({
            contentType: 'story',
            contentId: story.slug,
            wordIds: story.wordIds,
            wordStatuses,
            grammarConceptIds: story.grammarConceptIds,
            grammarStatuses
          })
        ])
      )
    },
    enabled: Boolean(user?.id && stories.length),
    staleTime: 1000 * 60 * 5
  })
}

export function PathPageClient({
  storyLearningIndex,
  grammarPathIndex
}: {
  storyLearningIndex: StoryLearningIndexItem[]
  grammarPathIndex: GrammarPathIndexItem[]
}) {
  const { user, isLoading: authLoading } = useAuth()
  const signedIn = Boolean(user)
  const { data: stats } = useDashboardStats()
  const { data: readerItems = [], isLoading: readerLoading } =
    useReaderContentSummaries()
  const { data: documents = [], isLoading: documentsLoading } =
    useDocumentSummaries()
  const { data: mediaHistory = [] } = useMediaHistory('youtube')
  const { data: preferences, isLoading: preferencesLoading } =
    useLearningPreferences()
  const upsertPreferences = useUpsertLearningPreferences()
  const [localProgress, setLocalProgress] =
    useState<LocalReaderProgressRecord | null>(null)

  const { data: recentDefaultStudyItems, isLoading: studyItemsLoading } =
    useQuery({
      queryKey: ['path-recent-default-study-items', user?.id],
      queryFn: () =>
        getRecentDefaultStudyItems({
          userId: user?.id ?? null,
          savedWordsLimit: 6,
          sentenceMiningLimit: 6
        }),
      enabled: Boolean(user?.id),
      staleTime: 1000 * 60 * 5
    })
  const savedStudyItems = useMemo(
    () => [
      ...(recentDefaultStudyItems?.savedWords ?? []),
      ...(recentDefaultStudyItems?.sentenceMining ?? [])
    ],
    [recentDefaultStudyItems]
  )
  const freshSavedStudyItems = useMemo(
    () => savedStudyItems.filter((item) => (item.reps ?? 0) === 0),
    [savedStudyItems]
  )

  const pathGrammarItems = useMemo<PathGrammarItem[]>(
    () =>
      grammarPathIndex.map((item) => ({
        id: item.conceptId,
        order: item.order,
        title: item.title,
        shortTitle: item.shortTitle,
        summary: item.summary,
        href: item.href,
        form: item.form,
        difficulty: item.difficulty
      })),
    [grammarPathIndex]
  )
  const pathStoryItems = useMemo<PathStoryItem[]>(
    () =>
      storyLearningIndex.map((story) => ({
        slug: story.slug,
        title: story.title_en,
        simplifiedTitle: story.simplifiedTitle,
        level: story.level,
        curriculumConceptId: story.curriculumConceptId,
        isPremiumChapter: story.isPremiumChapter,
        wordCount: story.wordCount,
        grammarConceptIds: story.grammarConceptIds,
        coverImage: story.coverImage
      })),
    [storyLearningIndex]
  )

  const pathSections = useMemo(
    () =>
      buildPathSections({
        grammarItems: pathGrammarItems,
        stories: pathStoryItems,
        dueReviewCount: stats?.cardsDueToday ?? 0
      }),
    [pathGrammarItems, pathStoryItems, stats?.cardsDueToday]
  )
  const pathLocators = useMemo(() => {
    const grammarLocators = grammarPathIndex.map((item) =>
      grammarLearningLocator(item.conceptId)
    )
    const storyLocators = storyLearningIndex.map((story) =>
      storyLearningLocator(story.slug)
    )
    return [...grammarLocators, ...storyLocators]
  }, [grammarPathIndex, storyLearningIndex])
  const { data: learningStates = new Map() } = useLearningStates(pathLocators)
  const pathSummary = useMemo(
    () =>
      resolvePathSections({
        sections: pathSections,
        learningStates,
        dueReviewCount: stats?.cardsDueToday ?? 0,
        startNodeId: preferences?.start_node_id ?? null
      }),
    [
      learningStates,
      pathSections,
      preferences?.start_node_id,
      stats?.cardsDueToday
    ]
  )

  const storyProgressResume = useMemo<ReaderResume | null>(() => {
    const candidates = storyLearningIndex
      .map((story) => {
        const state = learningStates.get(
          learningContentKey(storyLearningLocator(story.slug))
        )
        if (getStoryProgressKind(state) !== 'started') return null
        const progress = progressPercent(state?.progress)
        return {
          title: story.title_en,
          subtitle: progress === null ? 'Started' : `Started · ${progress}%`,
          href: `/read/${story.slug}`,
          sourceType: 'story',
          timestamp: new Date(state?.updated_at ?? 0).getTime()
        }
      })
      .filter((item): item is ReaderResumeCandidate => Boolean(item))
      .sort((a, b) => b.timestamp - a.timestamp)

    const best = candidates[0]
    return best
      ? {
          title: best.title,
          subtitle: best.subtitle,
          href: best.href,
          sourceType: best.sourceType
        }
      : null
  }, [learningStates, storyLearningIndex])
  const readerResume = useMemo(
    () =>
      getReaderResume(
        readerItems,
        documents,
        signedIn && localProgress?.sourceType === 'story' ? null : localProgress
      ),
    [documents, localProgress, readerItems, signedIn]
  )
  const resume = signedIn ? (storyProgressResume ?? readerResume) : readerResume
  const dueCount = stats?.cardsDueToday ?? 0
  const primaryHref =
    signedIn && dueCount > 0
      ? '/collections?id=due_today'
      : pathSummary.currentNode?.href || resume?.href || canonicalRoutes.read
  const primaryLabel =
    signedIn && dueCount > 0
      ? 'Review due cards'
      : pathSummary.currentNode
        ? 'Open current step'
        : resume
          ? 'Continue reading'
          : 'Start reading'

  useEffect(() => {
    if (authLoading) return
    analytics.todayViewed(signedIn)
  }, [authLoading, signedIn])

  useEffect(() => {
    const refresh = () => setLocalProgress(readRecentLocalReaderProgress())
    refresh()
    window.addEventListener(READER_PROGRESS_CHANGED_EVENT, refresh)
    window.addEventListener('storage', refresh)
    return () => {
      window.removeEventListener(READER_PROGRESS_CHANGED_EVENT, refresh)
      window.removeEventListener('storage', refresh)
    }
  }, [])

  useEffect(() => {
    if (
      !user?.id ||
      preferencesLoading ||
      preferences ||
      upsertPreferences.isPending
    ) {
      return
    }
    const onboardingState = readOnboardingState()
    if (!onboardingState.setupCompletedAt) return

    upsertPreferences.mutate({
      goal: onboardingState.goal ?? null,
      level: onboardingState.level ?? null
    })
  }, [preferences, preferencesLoading, upsertPreferences, user?.id])

  if (!signedIn && !authLoading) {
    return <GuestPath resume={resume} />
  }

  const shouldPromptForPreferences =
    signedIn &&
    preferences === null &&
    shouldShowExistingUserOnboardingPrompt(readOnboardingState())

  return (
    <PageLayout
      title="Home"
      description="Your next useful Chinese practice: lessons, stories, review, and your own reading."
      headerAction={
        <Button asChild>
          <Link href={primaryHref}>
            {primaryLabel}
            <IconArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      }
      headerActionCompact={
        <Button asChild size="sm" aria-label={primaryLabel}>
          <Link href={primaryHref}>
            <IconArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      }
    >
      <div className="mx-auto grid w-full max-w-7xl gap-6 px-4 py-5 pb-16 md:px-6 md:py-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <main className="min-w-0 space-y-5">
          <PathTopStrip
            completedCount={pathSummary.completedCount}
            totalCount={pathSummary.totalCount}
            progressRatio={pathSummary.progressRatio}
          />

          {shouldPromptForPreferences && <PersonalizePrompt />}

          <PathTrail
            sections={pathSummary.sections}
            storyLearningIndex={storyLearningIndex}
            onStartHere={(nodeId) =>
              upsertPreferences.mutate({ start_node_id: nodeId })
            }
            settingStart={upsertPreferences.isPending}
          />
        </main>

        <aside className="min-w-0 space-y-4 lg:sticky lg:top-20 lg:self-start">
          <StudyQueue
            dueCount={dueCount}
            items={freshSavedStudyItems}
            loading={studyItemsLoading || authLoading}
          />
          <YourLibrary
            readerItems={readerItems}
            documents={documents}
            mediaHistory={mediaHistory}
            loading={readerLoading || documentsLoading}
          />
        </aside>
      </div>
    </PageLayout>
  )
}

function PathTopStrip({
  completedCount,
  totalCount,
  progressRatio
}: {
  completedCount: number
  totalCount: number
  progressRatio: number
}) {
  return (
    <section className="bg-card/40 border-border/70 rounded-2xl border p-4 shadow-xs">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-muted-foreground text-xs font-semibold tracking-tight uppercase">
            Your progress
          </h2>
          <p className="mt-1 text-2xl font-bold tracking-tight">
            {percent(progressRatio)}% Complete
          </p>
        </div>
        <div className="w-full min-w-0 space-y-1.5 sm:max-w-[16rem]">
          <div className="text-muted-foreground flex items-center justify-between text-xs">
            <span>Overall completion</span>
            <span className="tabular-nums">
              {formatCount(completedCount)} / {formatCount(totalCount)} steps
            </span>
          </div>
          <Progress value={percent(progressRatio)} size="sm" />
        </div>
      </div>
    </section>
  )
}

function PersonalizePrompt() {
  return (
    <section className="bg-background rounded-lg border p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="font-semibold">Set your starting point</h2>
          <p className="text-muted-foreground mt-1 text-sm leading-6">
            Pick a goal and a comfortable first level so Home can start from a
            useful place.
          </p>
        </div>
        <Button asChild size="sm" className="shrink-0">
          <Link href={canonicalRoutes.onboarding}>Personalize</Link>
        </Button>
      </div>
    </section>
  )
}

function PathTrail({
  sections,
  storyLearningIndex,
  onStartHere,
  settingStart
}: {
  sections: ResolvedPathSection[]
  storyLearningIndex: StoryLearningIndexItem[]
  onStartHere: (nodeId: string) => void
  settingStart: boolean
}) {
  const foundCurrentSectionIndex = sections.findIndex(
    (section) => section.isCurrent
  )
  const currentSectionIndex =
    foundCurrentSectionIndex >= 0 ? foundCurrentSectionIndex : 0
  const currentSection = sections[currentSectionIndex] ?? null
  const volumeSummaries = Array.from(
    sections
      .reduce((groups, section) => {
        const existing = groups.get(section.volume)
        if (existing) {
          existing.stepCount += section.totalCount
        } else {
          groups.set(section.volume, {
            volume: section.volume,
            stepCount: section.totalCount
          })
        }
        return groups
      }, new Map<number, PathVolumeSummary>())
      .values()
  ).sort((a, b) => a.volume - b.volume)
  const currentVolume =
    currentSection?.volume ?? volumeSummaries[0]?.volume ?? 1
  const [selectedVolume, setSelectedVolume] = useState(currentVolume)
  const selectedVolumeExists = volumeSummaries.some(
    (summary) => summary.volume === selectedVolume
  )
  const activeVolume = selectedVolumeExists ? selectedVolume : currentVolume
  const visibleSections = useMemo(
    () => sections.filter((section) => section.volume === activeVolume),
    [activeVolume, sections]
  )
  const visibleStoryNodes = useMemo(() => {
    const slugs = new Set(
      visibleSections.flatMap((section) =>
        section.nodes.flatMap((node) =>
          node.storySlug ? [node.storySlug] : []
        )
      )
    )
    return storyLearningIndex.filter((story) => slugs.has(story.slug))
  }, [storyLearningIndex, visibleSections])
  const storyBySlug = useMemo(
    () => new Map(storyLearningIndex.map((story) => [story.slug, story])),
    [storyLearningIndex]
  )
  const { data: readinessBySlug = new Map() } =
    usePathStoryReadiness(visibleStoryNodes)
  const activeVolumeIndex = Math.max(
    0,
    volumeSummaries.findIndex((summary) => summary.volume === activeVolume)
  )
  const activeVolumeSummary = volumeSummaries[activeVolumeIndex] ?? null
  const previousVolume = volumeSummaries[activeVolumeIndex - 1] ?? null
  const nextVolume = volumeSummaries[activeVolumeIndex + 1] ?? null

  return (
    <section className="space-y-4" aria-label="Learning path">
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Lessons</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Volume {activeVolume}
            {activeVolumeSummary
              ? ` · ${formatCount(activeVolumeSummary.stepCount)} steps`
              : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!previousVolume}
            onClick={() => {
              if (previousVolume) setSelectedVolume(previousVolume.volume)
            }}
          >
            Previous volume
          </Button>
          {activeVolume !== currentVolume && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setSelectedVolume(currentVolume)}
            >
              Current volume
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!nextVolume}
            onClick={() => {
              if (nextVolume) setSelectedVolume(nextVolume.volume)
            }}
          >
            Next volume
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link href={canonicalRoutes.grammar}>All grammar</Link>
          </Button>
        </div>
      </div>

      <Accordion
        type="multiple"
        defaultValue={currentSection ? [currentSection.id] : undefined}
        className="border-border/70 border-t"
      >
        {visibleSections.map((section) => (
          <AccordionItem
            key={section.id}
            value={section.id}
            className="border-border/70 border-b last:border-b"
          >
            <AccordionTrigger className="hover:bg-muted/25 rounded-none px-2 py-4 hover:no-underline sm:px-3">
              <div className="flex flex-1 flex-col gap-3 pr-2 text-left sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-foreground text-base font-semibold">
                      {section.title}
                    </span>
                    {section.isCurrent && (
                      <Badge variant="secondary" size="sm">
                        Current
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground mt-0.5 text-xs font-normal">
                    {section.subtitle}
                  </p>
                </div>
                <div className="flex min-w-0 items-center gap-3 sm:w-36">
                  <div className="flex-1">
                    <Progress
                      value={
                        section.totalCount > 0
                          ? (section.completedCount / section.totalCount) * 100
                          : 0
                      }
                      size="sm"
                    />
                  </div>
                  <span className="text-muted-foreground shrink-0 text-xs font-medium tabular-nums">
                    {section.completedCount}/{section.totalCount} done
                  </span>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="border-border/60 border-t !pb-0">
              <PathSectionContent
                section={section}
                readinessBySlug={readinessBySlug}
                storyBySlug={storyBySlug}
                onStartHere={onStartHere}
                settingStart={settingStart}
              />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  )
}

function PathSectionContent({
  section,
  readinessBySlug,
  storyBySlug,
  onStartHere,
  settingStart
}: {
  section: ResolvedPathSection
  readinessBySlug: Map<string, ContentFamiliaritySummary>
  storyBySlug: Map<string, StoryLearningIndexItem>
  onStartHere: (nodeId: string) => void
  settingStart: boolean
}) {
  const reviewNodes = section.nodes.filter((node) => node.type === 'review')
  const grammarNodes = section.nodes.filter((node) => node.type === 'grammar')
  const storyNodes = section.nodes.filter(
    (node) => node.type === 'story_practice'
  )

  return (
    <div>
      {reviewNodes.length > 0 && (
        <div className="divide-border/65 divide-y px-4">
          {reviewNodes.map((node) => (
            <ReviewQueueRow key={node.id} node={node} />
          ))}
        </div>
      )}

      {grammarNodes.length > 0 && (
        <div className="divide-border/65 divide-y px-4">
          {grammarNodes.map((node, index) => (
            <PathGrammarRow
              key={node.id}
              node={node}
              index={index}
              onStartHere={onStartHere}
              settingStart={settingStart}
            />
          ))}
        </div>
      )}

      {storyNodes.length > 0 && (
        <div className="border-border/65 border-t px-4 py-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {storyNodes.map((node) => {
              const story = node.storySlug
                ? storyBySlug.get(node.storySlug)
                : undefined
              if (!story) return null
              const readiness = readinessBySlug.get(story.slug)

              return (
                <StoryCard
                  key={node.id}
                  story={story}
                  href={node.href}
                  readStoryText={
                    node.state === 'current'
                      ? 'Open current story'
                      : 'Read story'
                  }
                  learningState={{
                    status:
                      node.completion === 'done'
                        ? 'completed'
                        : node.completion === 'started'
                          ? 'studying'
                          : 'new',
                    progress: node.progress
                  }}
                  familiarityPercent={
                    readiness ? readiness.seenWordRatio * 100 : null
                  }
                  familiarityLabel="words known"
                  variant="compact"
                  className="h-full"
                />
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function ReviewQueueRow({ node }: { node: ResolvedPathNode }) {
  const isDueReview = node.title.toLowerCase().includes('due')

  return (
    <Link
      href={node.href}
      className="group hover:bg-muted/25 grid min-w-0 gap-3 py-3 transition sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center"
    >
      <span className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-full">
        <IconCards className="h-5 w-5" />
      </span>
      <span className="min-w-0">
        <span className="block font-semibold">{node.title}</span>
        <span className="text-muted-foreground mt-0.5 block text-sm">
          {node.subtitle || 'Saved words and sentences'}
        </span>
      </span>
      <span className="flex items-center gap-3 sm:justify-end">
        <Badge variant={isDueReview ? 'secondary' : 'neutral'}>
          {isDueReview ? 'Ready' : 'Saved'}
        </Badge>
        <span className="bg-foreground text-background flex h-8 w-8 items-center justify-center rounded-full transition group-hover:translate-x-0.5">
          <IconArrowRight className="h-4 w-4" />
        </span>
      </span>
    </Link>
  )
}

function PathGrammarRow({
  node,
  index,
  onStartHere,
  settingStart
}: {
  node: ResolvedPathNode
  index: number
  onStartHere: (nodeId: string) => void
  settingStart: boolean
}) {
  return (
    <div
      className={cn(
        'relative grid min-w-0 gap-3 py-3 transition sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center',
        node.state === 'current' && 'bg-primary/5 -mx-4 px-4',
        node.state !== 'current' && 'hover:bg-muted/20 -mx-4 px-4'
      )}
    >
      <span
        className={cn(
          'flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold tabular-nums',
          node.state === 'current'
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground'
        )}
      >
        {index + 1}
      </span>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="font-semibold">{node.title}</h4>
          <Badge variant={nodeStateVariant(node.state)}>
            {nodeStateLabel(node.state)}
          </Badge>
          {node.reason && (
            <Badge variant="neutral">
              {node.reason.replace(/^Learn /, '')}
            </Badge>
          )}
        </div>
        {node.subtitle && (
          <p className="text-muted-foreground mt-1 line-clamp-1 text-sm">
            {node.subtitle}
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-2 sm:justify-end">
        {node.state !== 'current' && node.state !== 'done' && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={settingStart}
            onClick={() => onStartHere(node.id)}
          >
            <IconFlag className="h-4 w-4" />
            Start here
          </Button>
        )}
        <Button
          asChild
          variant={node.state === 'current' ? 'default' : 'outline'}
          size="sm"
        >
          <Link href={node.href}>
            Open
            <IconArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  )
}

function nodeStateLabel(state: ResolvedPathNode['state']) {
  if (state === 'done') return 'Done'
  if (state === 'current') return 'Current'
  if (state === 'started') return 'Started'
  return 'Coming up'
}

function nodeStateVariant(
  state: ResolvedPathNode['state']
): 'success' | 'secondary' | 'outline' | 'neutral' {
  if (state === 'done') return 'success'
  if (state === 'current') return 'secondary'
  if (state === 'started') return 'outline'
  return 'neutral'
}

function StudyQueue({
  dueCount,
  items,
  loading
}: {
  dueCount: number
  items: StudyDeckItem[]
  loading: boolean
}) {
  return (
    <SidePanel
      icon={<IconTarget className="h-5 w-5" />}
      title="Review queue"
      actionHref={dueCount > 0 ? '/collections?id=due_today' : '/collections'}
      actionLabel={dueCount > 0 ? 'Review' : 'Decks'}
    >
      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : (
        <div>
          <div className="bg-muted/45 rounded-md px-3 py-2">
            <p className="font-semibold">{formatCount(dueCount)} due cards</p>
          </div>
          {items.length > 0 && (
            <div className="divide-border/60 mt-2 divide-y">
              {items.slice(0, 3).map((item) => (
                <Link
                  key={item.card_id}
                  href="/collections"
                  className="hover:bg-muted/45 -mx-2 block rounded-md px-2 py-2 transition"
                >
                  <span className="block truncate text-sm font-semibold">
                    {getStudyItemTitle(item)}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </SidePanel>
  )
}

function YourLibrary({
  readerItems,
  documents,
  mediaHistory,
  loading
}: {
  readerItems: StoredReaderContentSummary[]
  documents: UserDocumentSummary[]
  mediaHistory: UserMediaHistoryItem[]
  loading: boolean
}) {
  const recentReaderItems = [...readerItems]
    .sort((a, b) => getReaderTimestamp(b) - getReaderTimestamp(a))
    .slice(0, 4)
  const readerIds = new Set(recentReaderItems.map((item) => item.id))
  const unlinkedMedia = mediaHistory.filter(
    (item) => !item.reader_content_id || !readerIds.has(item.reader_content_id)
  )

  return (
    <SidePanel
      icon={<IconLanguage className="h-5 w-5" />}
      title="Your library"
      actionHref={canonicalRoutes.read}
      actionLabel="Open reader"
    >
      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : (
        <div className="space-y-2">
          {recentReaderItems.map((item) => (
            <ReaderLibraryItem key={item.id} item={item} />
          ))}
          {!recentReaderItems.length &&
            documents
              .slice(0, 2)
              .map((document) => (
                <PlainLibraryItem
                  key={document.id}
                  icon={<IconFileText className="h-4 w-4" />}
                  title={document.title}
                  href={`/read/document/${document.id}`}
                />
              ))}
          {unlinkedMedia.slice(0, 2).map((item) => (
            <PlainLibraryItem
              key={item.id}
              icon={<IconMusic className="h-4 w-4" />}
              title={item.title || 'YouTube song'}
              href={
                item.reader_content_id
                  ? `/read/lyrics/${item.reader_content_id}`
                  : canonicalRoutes.readMusic
              }
            />
          ))}
          {!recentReaderItems.length &&
            !documents.length &&
            !mediaHistory.length && (
              <div className="text-muted-foreground rounded-lg border border-dashed p-4 text-sm">
                Imported texts and songs will appear here.
              </div>
            )}
        </div>
      )}
    </SidePanel>
  )
}

function ReaderLibraryItem({ item }: { item: StoredReaderContentSummary }) {
  const href =
    item.source_type === 'lyrics'
      ? `/read/lyrics/${item.id}`
      : `/read/document/${item.id}`
  const icon =
    item.source_type === 'lyrics' ? (
      <IconMusic className="h-4 w-4" />
    ) : (
      <IconFileText className="h-4 w-4" />
    )

  return <PlainLibraryItem icon={icon} title={item.title} href={href} />
}

function PlainLibraryItem({
  icon,
  title,
  href
}: {
  icon: ReactNode
  title: string
  href: string
}) {
  return (
    <Link
      href={href}
      className="hover:bg-muted/45 flex min-w-0 items-center justify-between gap-3 rounded-md px-2 py-2 transition"
    >
      <span className="flex min-w-0 items-center gap-2">
        <span className="text-muted-foreground shrink-0">{icon}</span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">{title}</span>
        </span>
      </span>
      <IconArrowRight className="text-muted-foreground h-4 w-4 shrink-0" />
    </Link>
  )
}

function SidePanel({
  icon,
  title,
  actionHref,
  actionLabel,
  children
}: {
  icon: ReactNode
  title: string
  actionHref?: string
  actionLabel?: string
  children: ReactNode
}) {
  return (
    <section className="bg-card rounded-lg border p-3.5 shadow-xs">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="bg-muted text-muted-foreground flex h-8 w-8 shrink-0 items-center justify-center rounded-md">
            {icon}
          </span>
          <h2 className="truncate font-semibold">{title}</h2>
        </div>
        {actionHref && actionLabel && (
          <Button asChild variant="ghost" size="sm" className="shrink-0">
            <Link href={actionHref}>{actionLabel}</Link>
          </Button>
        )}
      </div>
      {children}
    </section>
  )
}

function GuestPath({ resume }: { resume: ReaderResume | null }) {
  return (
    <PageLayout
      title="Home"
      description="Start with reading, then sign in to keep your learning history synced."
      headerAction={
        <LoginDialog>
          <Button>
            Sign in to save progress
            <IconArrowRight className="h-4 w-4" />
          </Button>
        </LoginDialog>
      }
      headerActionCompact={
        <LoginDialog>
          <Button size="sm" aria-label="Sign in">
            <IconArrowRight className="h-4 w-4" />
          </Button>
        </LoginDialog>
      }
    >
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-4 py-5 pb-16 md:px-6 md:py-6">
        <section className="bg-card rounded-lg border p-5 shadow-sm">
          <Badge variant="secondary" size="md">
            <IconRoute className="h-4 w-4" />
            Guided reading
          </Badge>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight">
            Read first, then let Miaozi remember the trail.
          </h2>
          <p className="text-muted-foreground mt-2 max-w-2xl text-sm leading-6">
            Stories, grammar, saved words, imported texts, and songs come
            together after sign-in.
          </p>
        </section>

        {resume && (
          <section className="bg-card rounded-lg border p-5 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold">Continue reading</h2>
                <p className="text-muted-foreground mt-1 line-clamp-1 text-sm">
                  {resume.title}
                </p>
              </div>
              <Button asChild>
                <Link href={resume.href}>
                  Continue
                  <IconArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </section>
        )}

        <section className="grid gap-4 sm:grid-cols-2">
          <GuestAction
            icon={<IconBook2 className="h-5 w-5" />}
            title="Read stories"
            description="Beginner stories have tappable words and grammar notes."
            href={canonicalRoutes.read}
            label="Open stories"
          />
          <GuestAction
            icon={<IconPlus className="h-5 w-5" />}
            title="Bring your own text"
            description="Import a paragraph when you want help with real reading."
            href={canonicalRoutes.readImport}
            label="Import"
          />
        </section>
      </div>
    </PageLayout>
  )
}

function GuestAction({
  icon,
  title,
  description,
  href,
  label
}: {
  icon: ReactNode
  title: string
  description: string
  href: string
  label: string
}) {
  return (
    <section className="bg-card rounded-lg border p-5 shadow-sm">
      <span className="bg-muted text-muted-foreground flex h-10 w-10 items-center justify-center rounded-lg">
        {icon}
      </span>
      <h2 className="mt-4 text-lg font-semibold">{title}</h2>
      <p className="text-muted-foreground mt-1 text-sm leading-6">
        {description}
      </p>
      <Button asChild variant="outline" className="mt-4">
        <Link href={href}>
          {label}
          <IconArrowRight className="h-4 w-4" />
        </Link>
      </Button>
    </section>
  )
}
